<style lang="scss">
  @use "../../assets/css/design-system/colors";
  @use "../../assets/css/design-system/utils";
  @use "../../assets/css/design-system/mixins";

  $tab-panel-header-height: utils.in_rem(36px);
  $tab-panel-header-padding: 2px;
  $tab-panel-header-underline-height: 2.5px;

  .tab-panel {
    display: flex;
    flex-direction: column;
    overflow: auto;
    height: 100%;

    &.vertical {
      flex-direction: row;
      width: 100%;

      // We don't want a gap between the tab headers and the content for vertical styles in a modal
      &:not(.modal-vertical) {
        gap: utils.in_rem(24px);
      }
    }

    > ul.tab-panel-header {
      flex-grow: 0;
      flex-shrink: 1;
      margin: 0;
      gap: utils.in_rem(8px);
      padding: $tab-panel-header-padding;
      list-style: none;
      display: flex;
      justify-content: flex-start;
      flex-wrap: nowrap;

      // Allows horizontal scrolling of the tab headers only instead of the whole tab panel
      &:not(.vertical) {
        overflow-x: auto;
        overflow-y: hidden;
      }

      &.vertical {
        flex-direction: column;
        gap: utils.in_rem(4px);

        &.modal-vertical {
          padding: utils.in_rem(16px) utils.in_rem(8px);
          border-right: 1px solid colors.get(interfaceSwGrey_10);
        }
      }
    }

    > .tab-panel-body {
      flex-grow: 1;
      display: flex;
      flex-direction: column;
      overflow: auto;

      &:not(.vertical) {
        height: calc(
          100% - #{$tab-panel-header-height} - #{$tab-panel-header-padding} -
            #{$tab-panel-header-underline-height}
        );
      }
    }
  }
</style>

<script>
  import TabItem from "../atoms/TabItem.svelte";

  /**
   * @typedef {object} TabItem
   * @property {label} string
   * @property {slot} string
   * @property {(arg0: PointerEvent) => void} [onclick]
   * @property {boolean} [showPadlock]
   * @property {import("svelte").Snippet} [snippet] - Svelte 5
   */

  /** @type {array<TabItem>} tabItems */
  export let tabItems = [];

  let cls = "";
  export { cls as class };

  /** @type {"primary" | "secondary" | "secondary-pill" | "vertical"} */
  export let type = "primary";
  export let style = "";
  export let headerStyle = "";
  export let tabPanelBodyStyle = "";
  export let skeleton = false;
  export let postHeaderSnippet = null; // Add something to the end of the tab headers, e.g. a button

  // Adds top padding to vertical tabs, preventing the tab content from being padded.
  // Useful in a modal when the tab headers should be padded away from the modal header.
  // Also adds a 1px grey line to the right of the tab headers, separating them from content.
  export let modalVertical = false;

  /** @type {"small" | "large"} */
  export let size = "large";

  // You can bind to this to communicate the slot id of the active tab.
  export let activeTab = tabItems[0]?.slot;

  // revert to the first tab if the underlying tabItems change.
  $: if (!tabItems.map((t) => t.slot).includes(activeTab)) {
    activeTab = tabItems[0]?.slot;
  }

  /** @param {string} slot */
  const selectTab = (slot) => (e) => {
    activeTab = slot;
    const { onclick } = tabItems.find((tabItem) => tabItem.slot === slot);
    if (onclick) onclick(e);
  };

  // Get the active tab item for rendering its snippet
  $: activeTabItem = tabItems.find((tabItem) => tabItem.slot === activeTab);
</script>

<div
  class="tab-panel {cls} {type}"
  {style}
  class:modal-vertical={modalVertical}
>
  <ul
    class="tab-panel-header {type}"
    role="tablist"
    class:modal-vertical={modalVertical}
    style={headerStyle}
  >
    {#each tabItems as tabItem, i}
      {@const {
        label,
        slot = `tab${i}`,
        badge = "",
        badgeProps = {
          size: "large",
          type: "number",
          contrast: "high",
          inline: true,
        },
        showPadlock = false,
        leadingIcon = "",
      } = tabItem}
      <TabItem
        {leadingIcon}
        {label}
        {badge}
        {badgeProps}
        {showPadlock}
        {type}
        {skeleton}
        {size}
        {modalVertical}
        active={slot === activeTab}
        onclick={() => selectTab(slot)()}
      />
    {/each}
    {#if postHeaderSnippet}
      {@render postHeaderSnippet()}
    {/if}
  </ul>
  <!-- Dynamic tab content using Svelte 5 snippets -->
  <div class="tab-panel-body {type}" style={tabPanelBodyStyle}>
    {#if activeTabItem?.snippet}
      {@render activeTabItem.snippet()}
    {/if}
  </div>
</div>
